import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ChatMessage, ChatSentence } from '@/api/types';
import type { useFiloRemote } from '@/connection/FiloRemoteContext';
import { containsChatEndWord, splitSpeechText, trimChatHistory } from './chatCore';
import { requestChatResponse } from './chatApi';
import { createTranscribeCredentialsProvider } from '@/speech/transcribeCredentials';
import { useFaceDetector } from './useFaceDetector';
import { useTranscribeStream } from '@/speech/useTranscribeStream';

export type ChatPhase =
  | 'IDLE'
  | 'STARTING'
  | 'FACE_SEARCH'
  | 'GREETING'
  | 'LISTENING'
  | 'THINKING'
  | 'SPEAKING'
  | 'FAREWELL'
  | 'ENDED'
  | 'ERROR';

export type ChatLogEntry = {
  id: number;
  role: 'user' | 'assistant';
  text: string;
};

type ChatRemote = ReturnType<typeof useFiloRemote>;

export type UseChatAppControllerOptions = {
  robotId: string;
  speaker: number;
  stream: MediaStream | null;
  videoElement: HTMLVideoElement | null;
  remote: ChatRemote;
};

const GREETING_TEXT = 'おしゃべりしよう！オレンジにひかってるあいだはウィーゴのばん。ピロンってなったら、おはなししてね！';
const END_WORD_FAREWELL_TEXT = 'またね！';
const SILENCE_FAREWELL_TEXT = 'おしゃべりおわるね。またね！';
const LISTENING_TIMEOUT_MS = 30_000;
const CAMERA_STREAM_TIMEOUT_MS = 12_000;
const ECHO_GUARD_MS = 500;
const LISTEN_SOUND_GUARD_MS = 1_000; // 約0.90秒の効果音が終わってから集音を始める
const AUDIO_LEVEL_HISTORY_SIZE = 22;
const LED_LISTENING = { red: 138, green: 0, blue: 255, mode: 1 } as const;
const LED_THINKING = { red: 0, green: 120, blue: 255, mode: 1 } as const;
const LED_SPEAKING = { red: 255, green: 140, blue: 0, mode: 1 } as const;
const LED_OFF = { red: 0, green: 0, blue: 0, mode: 0 } as const;

type LedCommand =
  | typeof LED_LISTENING
  | typeof LED_THINKING
  | typeof LED_SPEAKING
  | typeof LED_OFF;

const ledCommandForPhase = (phase: ChatPhase): LedCommand => {
  if (phase === 'LISTENING') return LED_LISTENING;
  if (phase === 'THINKING') return LED_THINKING;
  if (phase === 'GREETING' || phase === 'SPEAKING' || phase === 'FAREWELL') {
    return LED_SPEAKING;
  }
  return LED_OFF;
};

const wait = (milliseconds: number) => (
  new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds))
);

const errorText = (error: unknown) => (
  error instanceof Error && error.message ? error.message : String(error)
);

export const normalizeAudioLevel = (rms: number) => {
  if (!Number.isFinite(rms) || rms <= 0) return 0;
  return Math.min(1, Math.max(0, (Math.log10(rms) + 4) / 2));
};

export function useChatAppController({
  robotId,
  speaker,
  stream,
  videoElement,
  remote,
}: UseChatAppControllerOptions) {
  const remoteRef = useRef(remote);
  remoteRef.current = remote;
  const [phase, setPhaseState] = useState<ChatPhase>('IDLE');
  const phaseRef = useRef<ChatPhase>('IDLE');
  const [logs, setLogs] = useState<ChatLogEntry[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conversationActive, setConversationActive] = useState(false);
  const [cameraLeaseAcquired, setCameraLeaseAcquired] = useState(false);
  const [audioLevels, setAudioLevels] = useState<number[]>(
    () => Array.from({ length: AUDIO_LEVEL_HISTORY_SIZE }, () => 0),
  );
  const [partialTranscript, setPartialTranscript] = useState('');
  const hasRequiredMediaTracks = (
    stream !== null
    && stream.getVideoTracks().length > 0
    && stream.getAudioTracks().length > 0
  );
  const historyRef = useRef<ChatMessage[]>([]);
  const leaseTokenRef = useRef<string | null>(null);
  const generationRef = useRef(0);
  const logIdRef = useRef(0);
  const silenceTimerRef = useRef<number | null>(null);
  const streamTimerRef = useRef<number | null>(null);
  const transitionBusyRef = useRef(false);
  const mountedRef = useRef(true);

  const invalidateGeneration = useCallback(() => {
    generationRef.current += 1;
  }, []);

  const setPhase = useCallback((next: ChatPhase) => {
    phaseRef.current = next;
    if (mountedRef.current) setPhaseState(next);
  }, []);

  const clearTimers = useCallback(() => {
    if (silenceTimerRef.current !== null) window.clearTimeout(silenceTimerRef.current);
    if (streamTimerRef.current !== null) window.clearTimeout(streamTimerRef.current);
    silenceTimerRef.current = null;
    streamTimerRef.current = null;
  }, []);

  const appendLog = useCallback((role: ChatLogEntry['role'], text: string) => {
    const entry = { id: ++logIdRef.current, role, text };
    setLogs((current) => [...current, entry]);
  }, []);

  const cleanup = useCallback(async () => {
    clearTimers();
    setConversationActive(false);
    setCameraLeaseAcquired(false);
    setAudioLevels(Array.from({ length: AUDIO_LEVEL_HISTORY_SIZE }, () => 0));
    setPartialTranscript('');
    transitionBusyRef.current = false;
    const token = leaseTokenRef.current;
    leaseTokenRef.current = null;
    if (token) await remoteRef.current.releaseCamera(token);
  }, [clearTimers]);

  const fail = useCallback(async (message: string, generation = generationRef.current) => {
    if (generation !== generationRef.current) return;
    await cleanup();
    if (generation !== generationRef.current || !mountedRef.current) return;
    setErrorMessage(message);
    setPhase('ERROR');
  }, [cleanup, setPhase]);

  const callSound = useCallback(async (
    sound: 'wake' | 'found' | 'listen' | 'thinking',
  ) => {
    try {
      await remoteRef.current.callRobot(
        robotId,
        'playSound',
        { sound },
        { timeoutMs: 3_000 },
      );
    } catch (error) {
      console.warn(`[ChatApp] playSound(${sound}) failed`, error);
    }
  }, [robotId]);

  const ledStateRef = useRef<LedCommand>(LED_OFF);

  const callLed = useCallback(async (command: LedCommand) => {
    try {
      await remoteRef.current.callRobot(
        robotId,
        'setLed',
        command,
        { timeoutMs: 3_000 },
      );
    } catch (error) {
      console.warn('[ChatApp] setLed failed', error);
    }
  }, [robotId]);

  const callLedRef = useRef(callLed);
  callLedRef.current = callLed;

  const speakText = useCallback(async (text: string) => {
    for (const chunk of splitSpeechText(text)) {
      await remoteRef.current.callRobot(
        robotId,
        'speak',
        { text: chunk, speaker },
        { timeoutMs: 60_000 },
      );
    }
  }, [robotId, speaker]);

  const speakSentence = useCallback(async (sentence: ChatSentence) => {
    const motion = sentence.motionId
      ? remoteRef.current.callRobot(
          robotId,
          'startMotion',
          Number.parseInt(sentence.motionId, 10),
          { timeoutMs: 60_000 },
        ).catch((error) => {
          console.warn(`[ChatApp] startMotion(${sentence.motionId}) failed`, error);
        })
      : Promise.resolve();
    await Promise.all([speakText(sentence.text), motion]);
  }, [robotId, speakText]);

  const finishFarewell = useCallback(async (
    text: string,
    generation = generationRef.current,
  ) => {
    if (generation !== generationRef.current || transitionBusyRef.current) return;
    transitionBusyRef.current = true;
    clearTimers();
    setPhase('FAREWELL');
    appendLog('assistant', text);
    historyRef.current = trimChatHistory([
      ...historyRef.current,
      { role: 'assistant', text },
    ]);
    try {
      await speakText(text);
    } catch (error) {
      console.warn('[ChatApp] farewell speech failed', error);
    }
    if (generation !== generationRef.current) return;
    await cleanup();
    if (generation !== generationRef.current || !mountedRef.current) return;
    setPhase('ENDED');
  }, [appendLog, cleanup, clearTimers, setPhase, speakText]);

  const scheduleListeningTimeout = useCallback(() => {
    if (silenceTimerRef.current !== null) window.clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = window.setTimeout(() => {
      silenceTimerRef.current = null;
      void finishFarewell(SILENCE_FAREWELL_TEXT);
    }, LISTENING_TIMEOUT_MS);
  }, [finishFarewell]);

  const beginListening = useCallback(async (generation: number) => {
    await wait(ECHO_GUARD_MS);
    if (generation !== generationRef.current || !mountedRef.current) return;
    await callSound('listen');
    if (generation !== generationRef.current || !mountedRef.current) return;
    await wait(LISTEN_SOUND_GUARD_MS);
    if (generation !== generationRef.current || !mountedRef.current) return;
    transitionBusyRef.current = false;
    setPhase('LISTENING');
    scheduleListeningTimeout();
  }, [callSound, scheduleListeningTimeout, setPhase]);

  const handleTranscript = useCallback((transcript: string) => {
    const generation = generationRef.current;
    if (
      phaseRef.current !== 'LISTENING'
      || transitionBusyRef.current
      || !transcript.trim()
    ) return;
    transitionBusyRef.current = true;
    clearTimers();
    setPartialTranscript('');
    appendLog('user', transcript);
    historyRef.current = trimChatHistory([
      ...historyRef.current,
      { role: 'user', text: transcript },
    ]);

    if (containsChatEndWord(transcript)) {
      transitionBusyRef.current = false;
      void finishFarewell(END_WORD_FAREWELL_TEXT, generation);
      return;
    }

    setPhase('THINKING');
    void callSound('thinking');
    void (async () => {
      try {
        const response = await requestChatResponse(
          { createChatResponse: (messages) => remoteRef.current.createChatResponse(messages) },
          historyRef.current,
        );
        if (generation !== generationRef.current) return;
        appendLog('assistant', response.text);
        historyRef.current = trimChatHistory([
          ...historyRef.current,
          { role: 'assistant', text: response.text },
        ]);
        setPhase('SPEAKING');
        for (const sentence of response.sentences) {
          await speakSentence(sentence);
          if (generation !== generationRef.current) return;
        }
        await beginListening(generation);
      } catch (error) {
        await fail(errorText(error), generation);
      }
    })();
  }, [
    appendLog,
    beginListening,
    callSound,
    clearTimers,
    fail,
    finishFarewell,
    setPhase,
    speakSentence,
  ]);

  const handleVoiceActivity = useCallback(() => {
    if (phaseRef.current === 'LISTENING') scheduleListeningTimeout();
  }, [scheduleListeningTimeout]);

  const handleAudioLevel = useCallback((rms: number) => {
    const level = normalizeAudioLevel(rms);
    setAudioLevels((current) => [...current.slice(1), level]);
  }, []);

  const getCredentials = useMemo(
    () => createTranscribeCredentialsProvider({
      createTranscribeCredentials: () => remoteRef.current.createTranscribeCredentials(),
    }),
    [],
  );

  useTranscribeStream({
    stream,
    active: conversationActive,
    captureEnabled: phase === 'LISTENING',
    getCredentials,
    onTranscript: handleTranscript,
    onPartialTranscript: setPartialTranscript,
    onAudioLevel: handleAudioLevel,
    onVoiceActivity: handleVoiceActivity,
    onError: (message) => {
      if (conversationActive) void fail(message);
    },
  });

  useEffect(() => {
    const command = ledCommandForPhase(phase);
    if (ledStateRef.current === command) return;
    ledStateRef.current = command;
    void callLed(command);
  }, [callLed, phase]);

  useEffect(() => () => {
    ledStateRef.current = LED_OFF;
    void callLedRef.current(LED_OFF);
  }, []);

  const faceDetector = useFaceDetector(videoElement, phase === 'FACE_SEARCH');

  const start = useCallback(async () => {
    const generation = ++generationRef.current;
    await cleanup();
    if (generation !== generationRef.current || !mountedRef.current) return;
    historyRef.current = [];
    logIdRef.current = 0;
    setLogs([]);
    setErrorMessage(null);
    setPhase('STARTING');
    try {
      await callSound('wake');
      if (generation !== generationRef.current) return;
      const token = await remoteRef.current.acquireCamera(robotId);
      if (!token) throw new Error('対象ロボットのカメラを開始できません');
      if (generation !== generationRef.current) {
        await remoteRef.current.releaseCamera(token);
        return;
      }
      leaseTokenRef.current = token;
      setCameraLeaseAcquired(true);
      streamTimerRef.current = window.setTimeout(() => {
        streamTimerRef.current = null;
        void fail('ロボットの映像と音声を受信できません', generation);
      }, CAMERA_STREAM_TIMEOUT_MS);
    } catch (error) {
      await fail(errorText(error), generation);
    }
  }, [callSound, cleanup, fail, robotId, setPhase]);

  useEffect(() => {
    if (
      phase !== 'STARTING'
      || !cameraLeaseAcquired
      || !hasRequiredMediaTracks
    ) return;
    if (streamTimerRef.current !== null) window.clearTimeout(streamTimerRef.current);
    streamTimerRef.current = null;
    setPhase('FACE_SEARCH');
  }, [cameraLeaseAcquired, hasRequiredMediaTracks, phase, setPhase]);

  useEffect(() => {
    if (phase !== 'FACE_SEARCH' || faceDetector.status !== 'error') return;
    void fail(faceDetector.errorMessage ?? '顔検出を開始できません');
  }, [faceDetector.errorMessage, faceDetector.status, fail, phase]);

  useEffect(() => {
    if (
      phase !== 'FACE_SEARCH'
      || !faceDetector.stableFaceDetected
      || transitionBusyRef.current
    ) return;
    const generation = generationRef.current;
    transitionBusyRef.current = true;
    setPhase('GREETING');
    setConversationActive(true);
    void (async () => {
      try {
        await callSound('found');
        if (generation !== generationRef.current) return;
        appendLog('assistant', GREETING_TEXT);
        historyRef.current = [];
        await speakText(GREETING_TEXT);
        await beginListening(generation);
      } catch (error) {
        await fail(errorText(error), generation);
      }
    })();
  }, [
    appendLog,
    beginListening,
    callSound,
    faceDetector.stableFaceDetected,
    fail,
    phase,
    setPhase,
    speakText,
  ]);

  const stopImmediately = useCallback(async () => {
    ++generationRef.current;
    try {
      await remoteRef.current.callRobot(
        robotId,
        'stopSpeak',
        undefined,
        { timeoutMs: 3_000 },
      );
    } catch {
      // Cleanup must continue even when the previous APK or transport cannot stop speech.
    }
    await cleanup();
    if (mountedRef.current) setPhase('ENDED');
  }, [cleanup, robotId, setPhase]);

  const retry = useCallback(() => {
    void start();
  }, [start]);

  useEffect(() => {
    mountedRef.current = true;
    void start();
    return () => {
      mountedRef.current = false;
      invalidateGeneration();
      clearTimers();
      const token = leaseTokenRef.current;
      leaseTokenRef.current = null;
      if (token) void remoteRef.current.releaseCamera(token);
    };
  }, [clearTimers, invalidateGeneration, start]);

  return {
    phase,
    logs,
    errorMessage,
    faceDetectorStatus: faceDetector.status,
    audioLevels,
    partialTranscript,
    retry,
    stopImmediately,
  };
}
